import {useConfig} from '@aboutyou/config';
import {Sponsored} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product_sponsored.types';
import {SponsoredContentSection} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/sections.types';
import {
  originClick,
  TrackingPromotionCreative,
} from '@aboutyou/legacy-tracking-sdk';
import {TrackPromotions, useTrackOriginClick} from '@aboutyou/tracking';
import {
  BrandLogo,
  Button,
  COLORS,
  desktopAndUpLinaria,
  FONT_SIZES,
  FONT_WEIGHTS,
  GenericIconName,
  Icon,
  largeScreensLinaria,
  mediaTabletAndUp,
  tabletAndUpLinaria,
  Text,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React from 'react';
import {STREAM_TOP_SPACING} from '../../backend-services/Header/constants';
import {
  Content,
  Header,
  HorizontalSponsoredPlacementContainer,
} from '../SponsoredPlacement/HorizontalSponsoredPlacementContainer';
import {useSponsoredBannerTrackingKey} from './utils/useSponsoredBannerTrackingKey';

export const SponsoredBanner: React.FC<SponsoredContentSection> = ({
  color,
  cta,
  headline,
  image,
  promotionTracking,
  sponsored,
  brand,
  targetUrl,
}) => {
  const {device} = useConfig();
  const trackOriginClick = useTrackOriginClick();
  const trackPromotionKey = useSponsoredBannerTrackingKey(
    promotionTracking.promotionId,
  );

  return (
    <TrackPromotions
      key={trackPromotionKey}
      disabled={!trackPromotionKey}
      promotion={{
        advertisement: {
          id: Number(promotionTracking.destination?.split('|')[1]),
          name: promotionTracking.promotionName,
          path: targetUrl.url,
          promotionId: promotionTracking.promotionId,
          locationId: promotionTracking.locationId,
          creativeName: promotionTracking.creativeName,
        },
        source: TrackingPromotionCreative.BasCategoryHeader,
      }}
      streamPosition={0}
      threshold={0.5}
      triggerOnce
    >
      <StyledHorizontalSponsoredPlacementContainer
        contentAlignment="start"
        color={color}
        image={image}
        targetUrl={targetUrl.url}
        onClick={() => {
          trackOriginClick(promotionTracking.locationId as originClick)();
        }}
        promotionTracking={promotionTracking}
        brand={brand}
        sponsored={sponsored}
      >
        <Wrapper>
          <Headline color="White">{headline}</Headline>
          {cta?.label && (
            <CTA
              aria-label={undefined}
              variant="glassy"
              size="fit-width"
              aria-hidden={device === 'mobile'}
            >
              {cta.label}
              <Icon
                asset={GenericIconName.RightArrowSharpEdges}
                size="XS"
                fill="White"
              />
            </CTA>
          )}
        </Wrapper>
        {brand && (
          <StyledBrandLogo
            $hasImage={!!brand.deprecatedWhiteLogoImage}
            name={brand.name}
            image={brand.deprecatedWhiteLogoImage}
            maxHeight="40px"
            showTooltip={false}
          />
        )}
      </StyledHorizontalSponsoredPlacementContainer>
    </TrackPromotions>
  );
};

const StyledHorizontalSponsoredPlacementContainer = styled(
  HorizontalSponsoredPlacementContainer,
)<{sponsored?: Sponsored}>`
  aspect-ratio: 4 / 1.2;
  border-radius: 0;

  @media ${mediaTabletAndUp} {
    aspect-ratio: 5 / 1;
    border-radius: 4px;
    margin-bottom: ${STREAM_TOP_SPACING}px;
  }

  ${Header} {
    --spacing: 8px;

    @media ${mediaTabletAndUp} {
      --spacing: 12px;
    }
  }

  ${Content} {
    top: ${props => (props.sponsored ? '20px' : 0)};
    padding-block: 0;
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: min(400px, 40%);
  padding-left: 4px;

  ${tabletAndUpLinaria(`
    padding-left: 22px;
    gap: 12px;
  `)}

  ${largeScreensLinaria(`
    gap: 24px;
  `)}
`;

const Headline = styled(Text)`
  font-size: ${FONT_SIZES.px14};
  font-weight: ${FONT_WEIGHTS.mediumHeavy};
  line-height: 1.5;
  z-index: 1;

  ${desktopAndUpLinaria(`
    font-size: ${FONT_SIZES.px20};
    line-height: 1.2;
  `)}

  ${largeScreensLinaria(`
    font-size: ${FONT_SIZES.px24};
  `)}
`;

const CTA = styled(Button)`
  &&&& {
    display: none;
    white-space: nowrap;

    ${desktopAndUpLinaria(`
    display: flex;
    `)}
  }
`;

const StyledBrandLogo = styled(BrandLogo)<{$hasImage: boolean}>`
  position: absolute;
  bottom: 12px;
  right: 12px;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  width: auto;
  height: auto;
  z-index: 1;
  color: ${COLORS.White};
  font-size: ${FONT_SIZES.px18};
  font-weight: ${FONT_WEIGHTS.heavy};
  max-width: ${props => (props.$hasImage ? '20%' : 'min-content')};

  ${tabletAndUpLinaria(`
    bottom: 16px;
    right: 16px;
    max-width: unset;
    height: 24px;
    font-size: ${FONT_SIZES.px24};
  `)}

  ${desktopAndUpLinaria(`
    height: 40px;
    font-size: ${FONT_SIZES.px32};
  `)}
`;
